/**
 * NotificationCenter — inbox persistente del owner (DB-backed).
 *
 * Antes: localStorage + polling de órdenes (fire-and-forget, se perdía si push fallaba).
 * Ahora: DB-backed via sm_notifications. Cada evento tiene fuente de verdad persistida.
 */
import { useNavigate } from "@tanstack/react-router";
import { Bell, CheckCheck, ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { cn } from "@/lib/utils";

interface NotifRow {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  read: boolean;
  createdAt: string;
}

interface NotifResponse { notifications: NotifRow[]; unreadCount: number }

const TYPE_ICON: Record<string, string> = {
  new_order: "🛒",
  order_confirmed: "✅",
  order_ready: "🎉",
  order_delivered: "🏠",
  order_canceled: "❌",
  order_returned: "↩️",
  loyalty_earned: "⭐",
  gift_card_issued: "🎁",
  platform: "📢",
  billing: "💳",
  system: "⚙️",
};

export function NotificationCenter({ tenantId, tenantName }: { tenantId: string; tenantName?: string }) {
  const navigate = useNavigate();
  const [data, setData] = useState<NotifResponse>({ notifications: [], unreadCount: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const d = await apiGetJson<NotifResponse>("/api/notifications", tenantId);
      setData(d);
    } catch { /* polling best-effort */ }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(t);
  }, [refresh]);

  // Cuando se abre, refresca y marca las unread como leídas tras un breve delay.
  useEffect(() => {
    if (!open || data.unreadCount === 0) return;
    const timer = setTimeout(async () => {
      try {
        await apiSendJson(`/api/notifications?action=read-all`, "POST", {}, tenantId);
        setData((d) => ({
          notifications: d.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      } catch { /* best-effort */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [open, data.unreadCount, tenantId]);

  const handleClick = async (n: NotifRow) => {
    if (!n.read) {
      await apiSendJson("/api/notifications", "POST", { ids: [n.id] }, tenantId).catch(() => {});
      setData((d) => ({
        ...d,
        notifications: d.notifications.map((x) => x.id === n.id ? { ...x, read: true } : x),
        unreadCount: Math.max(0, d.unreadCount - 1),
      }));
    }
    if (n.url) {
      if (n.url.startsWith("/")) {
        setOpen(false);
        void navigate({ to: n.url as never });
      } else {
        window.open(n.url, "_blank", "noopener");
      }
    }
  };

  const icon = (type: string) => TYPE_ICON[type] ?? "🔔";
  const notifs = data.notifications;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="relative shrink-0">
          <Bell className="h-5 w-5" />
          <span className="sr-only">Notificaciones</span>
          {data.unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {data.unreadCount > 9 ? "9+" : data.unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] p-0" align="end">
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm font-semibold">Notificaciones</p>
            {data.unreadCount > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">{data.unreadCount}</span>
            )}
            {tenantName && (
              <span className="truncate text-[11px] text-muted-foreground">· {tenantName}</span>
            )}
          </div>
          {data.unreadCount > 0 && (
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-[11px] text-muted-foreground"
              onClick={async () => {
                await apiSendJson(`/api/notifications?action=read-all`, "POST", {}, tenantId).catch(() => {});
                setData((d) => ({ notifications: d.notifications.map((n) => ({ ...n, read: true })), unreadCount: 0 }));
              }}>
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas
            </Button>
          )}
        </div>

        <div className="max-h-[26rem] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : notifs.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
              <p className="text-xs text-muted-foreground">Sin notificaciones todavía.</p>
            </div>
          ) : (
            notifs.map((n) => (
              <button key={n.id} type="button"
                className={cn(
                  "flex w-full items-start gap-2.5 border-b border-border/60 px-3 py-2.5 text-left text-xs transition-colors hover:bg-accent/50",
                  !n.read && "bg-primary/5",
                )}
                onClick={() => handleClick(n)}>
                <span className="mt-0.5 shrink-0 text-base leading-none">{icon(n.type)}</span>
                <div className="min-w-0 flex-1">
                  <p className={cn("text-foreground", !n.read && "font-semibold")}>{n.title}</p>
                  {n.body && <p className="mt-0.5 line-clamp-2 text-muted-foreground">{n.body}</p>}
                  <p className="mt-1 text-[10px] text-muted-foreground/70">
                    {new Date(n.createdAt).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}
                  </p>
                </div>
                {n.url && <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />}
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
